import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Automatischer Gratis-Artikel per Regel (Metaobject gift_rule).
 * Fuegt hinzu / entfernt nur Zeilen mit Property _gift_rule. Der Preis 0 EUR kommt aus einem
 * Admin-Rabatt; Properties sind Kundeneingaben und werden nie fuer Preise/Berechtigungen genutzt.
 */

const SOURCE = 'gift-auto-add';
const DEBOUNCE_MS = 300;
const SKIP_PREFIX = 'gift_skip_';
const ADDED_PREFIX = 'gift_added_';

/** @param {string} key @param {string | null} [value] */
function store(key, value) {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (_) {
    // no-op
  }
  return null;
}

/** @returns {string[]} */
function storedGiftKeys() {
  try {
    return Object.keys(sessionStorage).filter((k) => k.startsWith(SKIP_PREFIX) || k.startsWith(ADDED_PREFIX));
  } catch (_) {
    return [];
  }
}

/** @param {string} handle */
function resetFlags(handle) {
  store(ADDED_PREFIX + handle, null);
  store(SKIP_PREFIX + handle, null);
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

/** @param {string} url @param {object} body */
async function postJson(url, body) {
  const response = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    // keine JSON-Antwort
  }
  return { ok: response.ok, status: response.status, data };
}

async function fetchGiftCheck() {
  try {
    const response = await fetch('/?sections=gift-check', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const sections = await response.json();
    const html = sections && sections['gift-check'];
    if (typeof html !== 'string') throw new Error('Section fehlt');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('script[data-gift-check]');
    if (!script) throw new Error('Script-Tag fehlt');
    const parsed = JSON.parse(script.textContent || '');
    return {
      rules: Array.isArray(parsed.rules) ? parsed.rules : [],
      stale: Array.isArray(parsed.stale_gift_lines) ? parsed.stale_gift_lines : [],
      paid: Array.isArray(parsed.paid_gift_lines) ? parsed.paid_gift_lines : [],
    };
  } catch (error) {
    console.warn('[gift-auto-add] Pruefung nicht moeglich', error);
    return null;
  }
}

/** @param {string} key */
async function removeLine(key) {
  const { ok } = await postJson(Theme.routes.cart_change_url, { id: key, quantity: 0 });
  return ok;
}

/**
 * Holt das vollstaendige Cart-Objekt (inkl. Rabatten) und optional die Cart-Sections.
 * @returns {Promise<any | null>}
 */
async function refreshCart() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  const body = /** @type {Record<string, unknown>} */ ({ updates: {} });
  if (ids.length > 0) {
    body.sections = ids.join(',');
    body.sections_url = window.location.pathname;
  }
  const { ok, data } = await postJson(Theme.routes.cart_update_url, body);
  if (!ok || !data || !Array.isArray(data.items)) return null;
  return data;
}

/**
 * Entfernt eine Geschenkzeile, die Geld kostet, und sperrt die Regel fuer diese Session.
 * Der Kunde darf nie einen Gratis-Artikel bezahlen.
 * @param {string} key @param {string} handle
 */
async function removePaidGiftLine(key, handle) {
  console.warn(`[gift-auto-add] Rabatt greift nicht für Regel ${handle}`);
  store(SKIP_PREFIX + handle, '1');
  try {
    return await removeLine(key);
  } catch (error) {
    console.warn('[gift-auto-add] Entfernen nach Preispruefung fehlgeschlagen', error);
    return false;
  }
}

/**
 * Preispruefung auf dem endgueltigen Cart.
 * @param {any} cart
 * @returns {Promise<boolean>} true, wenn Zeilen entfernt wurden
 */
async function removePaidGiftLines(cart) {
  let removed = false;
  for (const item of cart.items) {
    const handle = item && item.properties && item.properties._gift_rule;
    if (!handle) continue;
    if (!(item.final_line_price > 0)) continue;
    if (await removePaidGiftLine(item.key, String(handle))) removed = true;
  }
  return removed;
}

/**
 * Feuert das Theme-Event, ohne dass der Cart-Drawer (auto-open) aufklappt.
 * @param {any} cart
 */
function dispatchCartUpdate(cart) {
  const drawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
  drawers.forEach((el) => el.removeAttribute('auto-open'));
  try {
    document.dispatchEvent(
      new CartUpdateEvent({}, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections: cart.sections })
    );
  } finally {
    drawers.forEach((el) => el.setAttribute('auto-open', ''));
  }
}

async function run() {
  const check = await fetchGiftCheck();
  if (!check) return;
  const { rules, stale, paid } = check;

  // 6. Reset: Flags von Regeln, die fuer diesen Warenkorb nicht (mehr) relevant sind
  const presentHandles = new Set(rules.map((rule) => String(rule.handle)));
  for (const key of storedGiftKeys()) {
    const handle = key.startsWith(SKIP_PREFIX) ? key.slice(SKIP_PREFIX.length) : key.slice(ADDED_PREFIX.length);
    if (!presentHandles.has(handle)) resetFlags(handle);
  }

  if (rules.length === 0 && stale.length === 0 && paid.length === 0) return;

  let changed = false;

  // 3. Veraltete Geschenkzeilen entfernen (sequenziell, change.js nimmt eine Zeile)
  for (const key of stale) {
    if (typeof key !== 'string' || !key) continue;
    try {
      if (await removeLine(key)) changed = true;
    } catch (error) {
      console.warn('[gift-auto-add] Entfernen fehlgeschlagen', error);
    }
  }

  // 5b. Geschenkzeilen, die Geld kosten (Rabatt nachtraeglich weggefallen), entfernen
  for (const line of paid) {
    if (!line || typeof line.key !== 'string' || !line.key) continue;
    if (await removePaidGiftLine(line.key, String(line.handle))) changed = true;
  }

  /** @type {Array<{ id: number, quantity: number, properties: { _gift_rule: string } }>} */
  const toAdd = [];

  for (const rule of rules) {
    const handle = String(rule.handle);
    const skipKey = SKIP_PREFIX + handle;
    const addedKey = ADDED_PREFIX + handle;

    if (!(rule.qualifying_qty > 0)) {
      resetFlags(handle);
      continue;
    }
    if (rule.gift_present) continue;

    // 5. Kunde hat das Geschenk manuell entfernt
    if (rule.gift_line_key == null && store(addedKey)) {
      store(skipKey, '1');
      continue;
    }
    if (store(skipKey)) continue;

    toAdd.push({ id: rule.gift_variant_id, quantity: 1, properties: { _gift_rule: handle } });
  }

  // 4. Hinzufuegen in einem Request
  if (toAdd.length > 0) {
    try {
      const { ok, status, data } = await postJson(Theme.routes.cart_add_url, { items: toAdd });
      if (!ok || !data || data.status || !Array.isArray(data.items)) {
        console.warn('[gift-auto-add] Hinzufuegen fehlgeschlagen', status, data && data.description);
        for (const item of toAdd) store(SKIP_PREFIX + item.properties._gift_rule, '1');
      } else {
        const returnedHandles = new Set();
        for (const item of data.items) {
          const handle = item && item.properties && item.properties._gift_rule;
          if (!handle) continue;
          returnedHandles.add(handle);
          changed = true;
          store(ADDED_PREFIX + handle, '1');
        }
        // Regeln, die nicht in der Antwort auftauchen: nicht erneut versuchen
        for (const item of toAdd) {
          if (!returnedHandles.has(item.properties._gift_rule)) store(SKIP_PREFIX + item.properties._gift_rule, '1');
        }
      }
    } catch (error) {
      // Netzfehler: kein Skip, der naechste Cart-Event versucht es erneut (kein Loop, da ereignisgetrieben)
      console.warn('[gift-auto-add] Hinzufuegen fehlgeschlagen', error);
    }
  }

  // 7. Endgueltiges Cart holen, Preispruefung, Sections + Event
  if (!changed) return;
  try {
    let cart = await refreshCart();
    if (!cart) throw new Error('cart/update fehlgeschlagen');
    if (await removePaidGiftLines(cart)) {
      cart = await refreshCart();
      if (!cart) throw new Error('cart/update fehlgeschlagen');
    }
    dispatchCartUpdate(cart);
  } catch (error) {
    console.warn('[gift-auto-add] Aktualisierung fehlgeschlagen', error);
  }
}

/** Beim Seitenaufruf: leerer Warenkorb laut Header-Bubble → kein Request noetig. */
function cartKnownEmpty() {
  const count = document.querySelector('[ref="cartBubbleCount"]');
  if (!count) return false;
  const bubble = count.closest('.cart-bubble');
  if (bubble && bubble.classList.contains('visually-hidden')) return true;
  const text = (count.textContent || '').trim();
  return text === '' || text === '0';
}

if (!window.Shopify?.designMode) {
  let running = false;
  let again = false;
  /** @type {number | undefined} */
  let timer;

  const execute = async () => {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      await run();
    } catch (error) {
      console.warn('[gift-auto-add]', error);
    } finally {
      running = false;
      if (again) {
        again = false;
        schedule();
      }
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = window.setTimeout(execute, DEBOUNCE_MS);
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.data?.source === SOURCE) return;
    schedule();
  });

  const initial = () => {
    if (cartKnownEmpty()) {
      // Leerer Warenkorb: keine Regel relevant, alle Flags zuruecksetzen, kein Request
      for (const key of storedGiftKeys()) store(key, null);
      return;
    }
    schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initial, { once: true });
  else initial();
}
